import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import {
  Add,
  AdminPanelSettings,
  CheckCircle,
  DeliveryDining,
  Handyman,
  Language as LanguageIcon,
  PhoneIphone,
  Refresh,
  Search,
  Settings,
  Storefront,
  Translate,
  WarningAmber,
} from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import DataTableCard from '../../components/ui/DataTableCard';
import EnhancedDataGrid from '../../components/ui/EnhancedDataGrid';
import RowActions from '../../components/ui/RowActions';
import api from '../../services/api';
import { getTranslationCatalog, provisionLanguage } from '../../services/platform/localeService';
import { useI18n } from '../../i18n/I18nProvider';
import { isKeepAsEnglish } from '../../i18n/keepAsEnglish';
import { PLATFORM_PRIMARY } from '../../theme/brand';

const TEAL = PLATFORM_PRIMARY;
const SLATE = '#0f172a';

type FilterMode = 'all' | 'missing' | 'translated';
type AppId = 'admin' | 'customer' | 'partner' | 'vendor' | 'provider';

const FALLBACK_APPS: Array<{
  id: AppId;
  label: string;
  description: string;
  namespaces: string[];
  englishCount: number;
}> = [
  {
    id: 'admin',
    label: 'Admin Panel',
    description: 'Web admin — dashboard, settings, hubs, operations',
    namespaces: ['admin', 'common', 'food', 'grocery', 'transport', 'delivery', 'on_demand', 'events', 'video_consultation', 'booking'],
    englishCount: 0,
  },
  {
    id: 'customer',
    label: 'Customer App',
    description: 'Customer mobile — home, rides, food, grocery, profile',
    namespaces: ['mobile', 'transport_mobile', 'common'],
    englishCount: 0,
  },
  {
    id: 'partner',
    label: 'Partner App',
    description: 'Partner / driver mobile — jobs, earnings, profile',
    namespaces: ['partner', 'common'],
    englishCount: 0,
  },
  {
    id: 'vendor',
    label: 'Vendor App',
    description: 'Vendor / merchant mobile — orders, menu, earnings',
    namespaces: ['vendor', 'common'],
    englishCount: 0,
  },
  {
    id: 'provider',
    label: 'Provider App',
    description: 'On-demand service provider — jobs, quotes, availability, profile',
    namespaces: ['provider', 'common'],
    englishCount: 0,
  },
];

const APP_ICON: Record<AppId, typeof AdminPanelSettings> = {
  admin: AdminPanelSettings,
  customer: PhoneIphone,
  partner: DeliveryDining,
  vendor: Storefront,
  provider: Handyman,
};

async function fetchNamespaceMap(lang: string, namespace: string): Promise<Record<string, string>> {
  const res = await api.get(`/admin/translations/${lang}`, { params: { namespace } });
  return (res.data.data || {}) as Record<string, string>;
}

async function fetchAppMaps(lang: string, namespaces: string[]) {
  const maps = await Promise.all(namespaces.map((ns) => fetchNamespaceMap(lang, ns)));
  const merged: Record<string, { namespace: string; key: string; value: string }> = {};
  namespaces.forEach((ns, i) => {
    Object.entries(maps[i]).forEach(([key, value]) => {
      merged[`${ns}::${key}`] = { namespace: ns, key, value };
    });
  });
  return merged;
}

export default function TranslatorMasterPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { reloadTranslations, language: uiLanguage, t } = useI18n();

  const [language, setLanguage] = useState('en');
  const [appId, setAppId] = useState<AppId>('admin');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterMode>('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ namespace: 'admin', key: '', value: '' });
  const [banner, setBanner] = useState<string | null>(null);
  const [provisioningCode, setProvisioningCode] = useState<string | null>(null);
  /** Keep polling catalog after Auto-translate starts (background job). */
  const [watchCatalogUntil, setWatchCatalogUntil] = useState(0);

  const { data: catalog, isLoading: catalogLoading, refetch: refetchCatalog } = useQuery({
    queryKey: ['translation-catalog'],
    queryFn: getTranslationCatalog,
    refetchInterval: (query) => {
      const langs = query.state.data?.languages || [];
      if (langs.some((l) => l.provisioning)) return 4000;
      if (Date.now() < watchCatalogUntil) return 5000;
      return false;
    },
  });

  const languages = catalog?.languages || [];
  const apps = (catalog?.apps?.length ? catalog.apps : FALLBACK_APPS) as typeof FALLBACK_APPS;
  const selectedApp = apps.find((a) => a.id === appId) || apps[0] || FALLBACK_APPS[0];
  const appNamespaces = selectedApp.namespaces;
  // Avoid MUI out-of-range Select warning while catalog is still loading
  const languageSelectValue = languages.some((l) => l.code === language)
    ? language
    : (languages[0]?.code || '');

  useEffect(() => {
    if (!languages.length) return;
    if (!languages.some((l) => l.code === language)) {
      const def = languages.find((l) => l.isDefault)?.code || languages[0].code;
      setLanguage(def);
    }
  }, [languages, language]);

  const { data: enMerged = {}, isLoading: enLoading } = useQuery({
    queryKey: ['translations-app', 'en', selectedApp.id, appNamespaces.join(',')],
    queryFn: () => fetchAppMaps('en', appNamespaces),
  });

  const { data: langMerged = {}, isLoading: langLoading } = useQuery({
    queryKey: ['translations-app', language, selectedApp.id, appNamespaces.join(',')],
    queryFn: () => fetchAppMaps(language, appNamespaces),
    enabled: !!language,
  });

  const rows = useMemo(() => {
    const ids = new Set([...Object.keys(enMerged), ...Object.keys(langMerged)]);
    const q = search.trim().toLowerCase();
    return Array.from(ids)
      .map((id) => {
        const en = enMerged[id];
        const loc = langMerged[id];
        const namespace = en?.namespace || loc?.namespace || 'common';
        const key = en?.key || loc?.key || id;
        const english = en?.value || '';
        const value = loc?.value || '';
        const missing = !value || (language !== 'en' && value === english && !isKeepAsEnglish(english, key));
        return { id, namespace, key, english, value, missing };
      })
      .filter((row) => {
        if (filter === 'missing' && !row.missing) return false;
        if (filter === 'translated' && row.missing) return false;
        if (!q) return true;
        return (
          row.key.toLowerCase().includes(q) ||
          row.namespace.toLowerCase().includes(q) ||
          row.value.toLowerCase().includes(q) ||
          row.english.toLowerCase().includes(q)
        );
      })
      .sort((a, b) => a.namespace.localeCompare(b.namespace) || a.key.localeCompare(b.key));
  }, [enMerged, langMerged, search, filter, language]);

  const saveMutation = useMutation({
    mutationFn: () =>
      api.put('/admin/translations', {
        language,
        namespace: form.namespace,
        key: form.key,
        value: form.value,
      }),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['translations-app'] });
      queryClient.invalidateQueries({ queryKey: ['translation-catalog'] });
      if (language === uiLanguage) await reloadTranslations();
      setDialogOpen(false);
      setForm({ namespace: appNamespaces[0] || 'admin', key: '', value: '' });
    },
  });

  const provisionMutation = useMutation({
    mutationFn: ({ code, force }: { code: string; force: boolean }) =>
      provisionLanguage(code, force, false),
    onMutate: ({ code }) => setProvisioningCode(code),
    onSettled: () => setProvisioningCode(null),
    onSuccess: (result, vars) => {
      setWatchCatalogUntil(Date.now() + 3 * 60 * 1000);
      queryClient.invalidateQueries({ queryKey: ['translation-catalog'] });
      queryClient.invalidateQueries({ queryKey: ['locale-languages'] });
      setBanner(
        result?.started
          ? `Auto-translate started for ${vars.code.toUpperCase()}. Progress updates as keys are saved (coverage = actually translated, not just key count).`
          : `Auto-translate completed for ${vars.code.toUpperCase()}.`,
      );
      window.setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['translations-app'] });
        refetchCatalog();
      }, 6000);
      window.setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['translations-app'] });
        queryClient.invalidateQueries({ queryKey: ['translation-catalog'] });
      }, 45000);
    },
    onError: (_e, vars) => {
      setBanner(`Auto-translate failed for ${vars.code.toUpperCase()}. Check backend connection.`);
    },
  });

  const selectedLang = languages.find((l) => l.code === language);
  const missingInView = rows.filter((r) => r.missing).length;

  const columns: GridColDef[] = [
    {
      field: 'key',
      headerName: 'Key',
      flex: 1.2,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.78rem' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'english',
      headerName: 'English',
      flex: 1.3,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary" noWrap title={String(params.value || '')}>
          {params.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'value',
      headerName: language === 'en' ? 'Value' : `${language.toUpperCase()} translation`,
      flex: 1.4,
      minWidth: 220,
      renderCell: (params) => {
        const missing = params.row.missing && language !== 'en';
        return (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', overflow: 'hidden' }}>
            {missing && <Chip size="small" label="Missing" color="warning" variant="outlined" sx={{ height: 22 }} />}
            <Typography variant="body2" noWrap title={String(params.value || '')} sx={{ flex: 1 }}>
              {params.value || '—'}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 72,
      sortable: false,
      renderCell: (params) => (
        <RowActions
          onEdit={() => {
            setForm({
              namespace: params.row.namespace,
              key: params.row.key,
              value: params.row.value || params.row.english || '',
            });
            setDialogOpen(true);
          }}
        />
      ),
    },
  ];

  return (
    <PageShell>
      <PageHeader
        eyebrow="Localization"
        title="Translator Master"
        subtitle="Strings grouped by Admin Panel, Customer App, Partner App, and Vendor App. Add a language once — English converts automatically."
        actions={
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<Settings />}
              onClick={() => navigate('/config/masters?group=locale&type=languages')}
              sx={{ borderColor: alpha(TEAL, 0.35), color: TEAL, '&:hover': { borderColor: TEAL, bgcolor: alpha(TEAL, 0.06) } }}
            >
              {t('admin.translator.manage_languages', 'Manage languages')}
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                setForm({ namespace: appNamespaces[0] || 'admin', key: '', value: '' });
                setDialogOpen(true);
              }}
              sx={{ bgcolor: TEAL, '&:hover': { bgcolor: '#0d9488' } }}
            >
              {t('admin.translator.add_string', 'Add string')}
            </Button>
          </Stack>
        }
      />

      <Alert
        severity="info"
        icon={<LanguageIcon />}
        sx={{
          mb: 2.5,
          borderRadius: 2.5,
          border: `1px solid ${alpha(TEAL, 0.25)}`,
          bgcolor: alpha(TEAL, 0.06),
          color: SLATE,
          '& .MuiAlert-icon': { color: TEAL },
        }}
      >
        {t(
          'admin.translator.flow_hint',
          'Flow: Master Data → Languages (add/activate) → Auto-translate English → keys appear here by app → admin & mobile apps load active languages.',
        )}
      </Alert>

      {banner && (
        <Alert severity={banner.includes('failed') ? 'error' : 'success'} sx={{ mb: 2, borderRadius: 2 }} onClose={() => setBanner(null)}>
          {banner}
        </Alert>
      )}

      {/* Apps module board — Admin / Customer / Partner / Vendor / Provider */}
      <Box
        sx={{
          mb: 2.5,
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(5, 1fr)' },
        }}
      >
        {apps.map((app) => {
          const selected = app.id === selectedApp.id;
          const Icon = APP_ICON[app.id as AppId] || AdminPanelSettings;
          return (
            <Paper
              key={app.id}
              elevation={0}
              onClick={() => setAppId(app.id as AppId)}
              sx={{
                p: 2,
                borderRadius: 2.5,
                cursor: 'pointer',
                border: '2px solid',
                borderColor: selected ? TEAL : 'divider',
                bgcolor: selected ? alpha(TEAL, 0.05) : '#fff',
                transition: 'border-color .15s, box-shadow .15s',
                '&:hover': { borderColor: alpha(TEAL, 0.55), boxShadow: `0 8px 24px ${alpha(SLATE, 0.06)}` },
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="flex-start">
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: selected ? TEAL : alpha(SLATE, 0.06),
                    color: selected ? '#fff' : SLATE,
                    flexShrink: 0,
                  }}
                >
                  <Icon fontSize="small" />
                </Box>
                <Box minWidth={0} flex={1}>
                  <Typography variant="subtitle2" fontWeight={800} sx={{ color: SLATE }} noWrap>
                    {app.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {app.description}
                  </Typography>
                  <Typography variant="h5" fontWeight={800} sx={{ mt: 1, letterSpacing: '-0.03em', color: selected ? TEAL : SLATE }}>
                    {catalogLoading ? '…' : app.englishCount}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    English keys
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          );
        })}
      </Box>

      {/* Active languages */}
      <Paper elevation={0} sx={{ mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            bgcolor: alpha(TEAL, 0.05),
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={800} sx={{ color: SLATE }}>
              {t('admin.translator.active_languages', 'Active languages')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Shown in admin language switcher and Customer / Partner / Vendor app language screens
            </Typography>
          </Box>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={() => refetchCatalog()}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ p: 2, display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' } }}>
          {catalogLoading && !languages.length && (
            <Box sx={{ py: 4, gridColumn: '1 / -1', textAlign: 'center' }}>
              <CircularProgress size={28} sx={{ color: TEAL }} />
            </Box>
          )}
          {!catalogLoading && !languages.length && (
            <Alert severity="warning" sx={{ gridColumn: '1 / -1' }}>
              No active languages yet. Add languages under Master Data → Languages.
            </Alert>
          )}
          {languages.map((lang) => {
            const selected = language === lang.code;
            const busy = provisioningCode === lang.code || lang.provisioning;
            return (
              <Paper
                key={lang.code}
                elevation={0}
                onClick={() => setLanguage(lang.code)}
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                  cursor: 'pointer',
                  border: '2px solid',
                  borderColor: selected ? TEAL : 'divider',
                  bgcolor: selected ? alpha(TEAL, 0.04) : '#fff',
                  '&:hover': { borderColor: alpha(TEAL, 0.55) },
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="subtitle1" fontWeight={800} sx={{ color: SLATE }}>
                        {lang.nativeName || lang.name}
                      </Typography>
                      <Chip size="small" label={lang.code.toUpperCase()} sx={{ height: 22, fontWeight: 700 }} />
                      {lang.isDefault && <Chip size="small" label={t('admin.translator.default', 'Default')} color="primary" variant="outlined" sx={{ height: 22 }} />}
                      {lang.rtl && <Chip size="small" label={t('admin.translator.rtl', 'RTL')} sx={{ height: 22 }} />}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {lang.name}
                      {lang.code === 'en'
                        ? ` · ${lang.keyCount} source keys`
                        : ` · ${lang.translatedCount ?? 0}/${catalog?.englishKeys || lang.keyCount} translated · ${lang.coverage ?? 0}%`}
                    </Typography>
                  </Box>
                  {lang.provisioned && !busy && (lang.missingCount || 0) === 0 ? (
                    <CheckCircle sx={{ color: TEAL, fontSize: 20 }} />
                  ) : busy ? (
                    <CircularProgress size={18} sx={{ color: TEAL }} />
                  ) : lang.provisionError || (lang.missingCount || 0) > 0 ? (
                    <WarningAmber color="warning" fontSize="small" />
                  ) : null}
                </Stack>
                <LinearProgress
                  variant={busy ? 'indeterminate' : 'determinate'}
                  value={busy ? undefined : Math.min(100, lang.coverage || 0)}
                  sx={{
                    mt: 1.5,
                    height: 6,
                    borderRadius: 99,
                    bgcolor: alpha(TEAL, 0.12),
                    '& .MuiLinearProgress-bar': { bgcolor: TEAL, borderRadius: 99 },
                  }}
                />
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                  <Button
                    size="small"
                    variant={selected ? 'contained' : 'outlined'}
                    startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <Translate />}
                    disabled={busy || lang.code === 'en'}
                    onClick={(e) => {
                      e.stopPropagation();
                      // force=false: only missing / still-English keys (safe resume after rate limits)
                      provisionMutation.mutate({ code: lang.code, force: false });
                    }}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      ...(selected
                        ? { bgcolor: TEAL, '&:hover': { bgcolor: '#0d9488' } }
                        : { borderColor: alpha(TEAL, 0.4), color: TEAL }),
                    }}
                  >
                    {lang.code === 'en' ? 'Source' : busy ? t('admin.translator.provisioning', 'Translating…') : t('admin.translator.auto_translate', 'Auto-translate')}
                  </Button>
                  {(lang.stillEnglishCount || 0) > 0 && lang.code !== 'en' && (
                    <Chip size="small" color="warning" variant="outlined" label={`${lang.stillEnglishCount} still English`} />
                  )}
                  {(lang.absentCount || 0) > 0 && lang.code !== 'en' && (
                    <Chip size="small" color="warning" variant="outlined" label={`${lang.absentCount} absent`} />
                  )}
                </Stack>
                {lang.provisionError && !busy && (lang.missingCount || 0) > 0 && (
                  <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 1, lineHeight: 1.35 }}>
                    {lang.provisionError}
                  </Typography>
                )}
              </Paper>
            );
          })}
        </Box>
      </Paper>

      {/* Catalog for selected app */}
      <DataTableCard
        title={`${selectedApp.label} — ${t('admin.translator.catalog', 'Translation catalog')}`}
        subtitle={`${rows.length} keys · ${language.toUpperCase()}${missingInView && language !== 'en' ? ` · ${missingInView} missing` : ''}`}
        minHeight={520}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
          <TextField
            select
            size="small"
            label="Language"
            value={languageSelectValue}
            disabled={!languages.length}
            onChange={(e) => setLanguage(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            {!languages.length && (
              <MenuItem value="">
                {catalogLoading ? t('common.label.loading', 'Loading…') : t('admin.translator.no_languages', 'No languages')}
              </MenuItem>
            )}
            {languages.map((l) => (
              <MenuItem key={l.code} value={l.code}>
                {l.nativeName || l.name} ({l.code.toUpperCase()})
                {l.provisioned ? ' ✓' : ''}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            size="small"
            placeholder={t('admin.translator.search_placeholder', 'Search key or value…')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 220 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <ToggleButtonGroup
            exclusive
            size="small"
            value={filter}
            onChange={(_e, v) => v && setFilter(v)}
            sx={{
              '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, px: 1.5 },
              '& .Mui-selected': { bgcolor: `${alpha(TEAL, 0.12)} !important`, color: `${TEAL} !important` },
            }}
          >
            <ToggleButton value="all">{t('admin.translator.filter_all', 'All')}</ToggleButton>
            <ToggleButton value="missing">{t('admin.translator.filter_missing', 'Missing')}</ToggleButton>
            <ToggleButton value="translated">{t('admin.translator.filter_translated', 'Translated')}</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Viewing <strong>{selectedApp.label}</strong>
          {selectedLang ? ` · ${selectedLang.nativeName || selectedLang.name}` : ''} · {selectedApp.englishCount} English source keys
        </Typography>

        <EnhancedDataGrid
          rows={rows}
          columns={columns}
          loading={enLoading || langLoading}
          getRowId={(r) => r.id as string}
          autoHeight
          exportFileName={`translator-${selectedApp.id}-${language}`}
        />
      </DataTableCard>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {form.key ? t('admin.translator.edit', 'Edit translation') : t('admin.translator.add_string', 'Add string')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="App area"
                value={form.namespace}
                onChange={(e) => setForm((s) => ({ ...s, namespace: e.target.value }))}
              >
                {appNamespaces.map((ns) => (
                  <MenuItem key={ns} value={ns}>{ns}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('admin.translator.key', 'Key')}
                value={form.key}
                onChange={(e) => setForm((s) => ({ ...s, key: e.target.value }))}
                placeholder="e.g. nav.dashboard"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={`${t('admin.translator.value', 'Value')} (${language.toUpperCase()})`}
                value={form.value}
                multiline
                minRows={2}
                onChange={(e) => setForm((s) => ({ ...s, value: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>{t('common.action.cancel', 'Cancel')}</Button>
          <Button
            variant="contained"
            disabled={!form.key || !form.value || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
            sx={{ bgcolor: TEAL, '&:hover': { bgcolor: '#0d9488' } }}
          >
            {t('admin.translator.save', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}
